import './App.css';
import Headshot from './images/headshot.jpg';

const ME = 'Zachary Kent';

// Ordered newest first. Mirrors pubs.bib in the CV repo.
const publications = [
  {
    title: 'Parallelizing Congruence Closure',
    authors: [ME, 'Amar Shah'],
    venue: 'FMCAD 2026',
    links: [
      { label: 'doi', href: 'https://doi.org/10.34727/2026/isbn.978-3-85448-093-8_25' },
    ],
  },
  {
    title: 'μSTM: A Lightweight and Efficient STM Supporting General Types and Deferred Aborts',
    authors: [ME, 'Guy Blelloch', 'André Costa'],
    venue: 'SPAA 2026',
    links: [
      { label: 'doi', href: 'https://doi.org/10.1145/3816782.3819198' },
      { label: 'code', href: 'https://github.com/cmuparlay/ustm' },
    ],
  },
  {
    title: 'Big Atomics: Non-Blocking Algorithms with a Direct Fast Path',
    authors: ['Daniel Anderson', 'Guy Blelloch', ME, 'Siddhartha Jayanti'],
    venue: 'SPAA 2026',
    links: [
      { label: 'doi', href: 'https://doi.org/10.1145/3816782.3819220' },
      { label: 'mechanization', href: 'https://github.com/cmuparlay/smr-verification' },
    ],
  },
  {
    title: 'TLF: Transactional Lock Fusion',
    authors: ['Guy Blelloch', ME, 'Yuanhao Wei'],
    venue: 'SPAA 2025',
    links: [
      { label: 'doi', href: 'https://doi.org/10.1145/3694906.3743341' },
      { label: 'code', href: 'https://github.com/cmuparlay/fuse' },
    ],
  },
];

const preprints = [
  {
    title: 'Mechanized Metatheory of Forward Reasoning for End-to-End Linearizability Proofs',
    authors: [ME, 'Ugur Y. Yavuz', 'Siddhartha Jayanti', 'Stephanie Balzer', 'Guy Blelloch'],
    venue: 'arXiv 2025',
    links: [
      { label: 'arXiv', href: 'https://arxiv.org/abs/2509.06872' },
      { label: 'mechanization', href: 'https://github.com/cmuparlay/metaconfigurations' },
    ],
  },
];

const cmuProjects = [
  {
    when: '2026–Present',
    title: 'Deqalloc: The Memory-Stealing Allocator',
    text: 'A lock-free memory allocator based on work-stealing, specialized for concurrent programs. Currently under submission.',
  },
];

const cornellProjects = [
  {
    when: '2023–2026',
    title: 'JITNIC',
    where: 'under Nate Foster',
    text: 'A just-in-time compiler for eBPF targeting modern heterogeneous architectures. Program traces and state are partitioned at runtime between the host CPU and a SmartNIC.',
  },
  {
    when: '2024',
    title: 'Extending NumFuzz',
    where: 'under Justin Hsu',
    text: 'Added polymorphism over error bounds to NumFuzz, a language whose linear, effectful type system bounds floating-point error, and gave it a sound categorical semantics.',
  },
  {
    when: '2022–2023',
    title: 'Petr4',
    where: 'under Nate Foster',
    text: 'Contributed to Petr4, a verified P4 compiler in Rocq. Built a verified IR interpreter and proved several compiler passes semantics-preserving.',
  },
];

const industry = [
  {
    when: 'Summer 2026',
    title: 'Microsoft Research (RiSE)',
    where: 'Research Intern',
    text: 'Runtime monitoring of agent tool calls. Checked conformance of tool-call traces against a regex-based policy language, matched with symbolic derivatives.',
  },
  {
    when: 'Summer 2023',
    title: 'Apple',
    where: 'Software Engineering Intern',
    text: "Prototyped a JIT for Swift scripts built on LLVM's ORC API that defers both type-checking and code-generation to runtime. Contributed upstream to the Swift repository.",
  },
];

// Teaching Assistant positions, newest first.
const cmuTeaching = [
  ['15-440: Distributed Systems', 'Fall 2026'],
];

const cornellTeaching = [
  ['CS 4110: Programming Languages and Logics', 'Spring 2024'],
  ['CS 3110: Data Structures and Functional Programming', 'Fall 2022–Fall 2023'],
  ['CS 4820: Introduction to Analysis of Algorithms', 'Spring 2022'],
  ['CS 2800: Discrete Structures', 'Fall 2021'],
];

const Courses = ({ courses }) => (
  <ul>
    {courses.map(([course, term]) => (
      <li key={course}>{course} <span className="dim">({term})</span></li>
    ))}
  </ul>
);

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer">{children}</a>
);

const Authors = ({ names }) =>
  names.map((name, i) => (
    <span key={name}>
      {i > 0 && ', '}
      {name === ME ? <strong>{name}</strong> : name}
    </span>
  ));

const Paper = ({ title, authors, venue, links }) => (
  <li className="paper">
    <span className="paper-title"><Ext href={links[0].href}>{title}</Ext></span>
    <span className="meta"><Authors names={authors} /></span>
    <span className="meta">
      <span className="venue">{venue}</span>
      {links.map(l => (
        <span key={l.label}> · <Ext href={l.href}>{l.label}</Ext></span>
      ))}
    </span>
  </li>
);

const Entry = ({ when, title, where, text }) => (
  <li className="entry">
    <span className="when">{when}</span>
    <div>
      <strong>{title}</strong>
      {where && <span className="where">, {where}</span>}
      <p>{text}</p>
    </div>
  </li>
);

const App = () => (
  <div className="page">
    <h1>Zachary Kent</h1>

    <div className="intro">
      <img src={Headshot} alt="Zachary Kent" className="me" />
      <div>
        <p>
          I'm currently a third-year Ph.D. student at Carnegie Mellon University, advised
          by <Ext href="https://www.cs.cmu.edu/~guyb/">Guy Blelloch</Ext> and{' '}
          <Ext href="https://www.cs.cmu.edu/~balzers/">Stephanie Balzer</Ext>. I'm broadly
          interested in using programming languages and formal methods to make
          concurrent software fast and provably correct.
        </p>
        <p>
          During my Ph.D., I've explored this through two lenses: fast concurrent
          algorithms (including transactional memory), and machine-checked proofs in
          Iris and Rocq that such algorithms are correct. Lately, I've been thinking about how to modularly specify
          and prove classical progress conditions like lock-freedom in separation logic.
        </p>
        <p>
          Before CMU, I received my B.S. in Computer Science <em>summa cum laude</em> from
          Cornell University in 2024, where I was named a Merrill Presidential Scholar.
        </p>
        <p>
          Outside of research, I enjoy cycling, rowing (sweep and sculling), and
          reading speculative fiction.
        </p>
        <p className="links">
          <a href="mailto:zkent@cs.cmu.edu">Email</a> · <Ext href="https://github.com/zachary-kent">GitHub</Ext> · <Ext href="https://orcid.org/0009-0006-6930-2930">ORCID</Ext> · <Ext href={`${process.env.PUBLIC_URL}/Zachary-Kent-CV.pdf`}>CV</Ext>
        </p>
      </div>
    </div>

    <h2>Publications</h2>
    <ul className="papers">
      {publications.map(p => <Paper key={p.title} {...p} />)}
    </ul>

    <h3>Preprints</h3>
    <ul className="papers">
      {preprints.map(p => <Paper key={p.title} {...p} />)}
    </ul>

    <h2>Other Projects</h2>
    <h3>At CMU</h3>
    <ul className="entries">
      {cmuProjects.map(e => <Entry key={e.title} {...e} />)}
    </ul>

    <h3>At Cornell</h3>
    <ul className="entries">
      {cornellProjects.map(e => <Entry key={e.title} {...e} />)}
    </ul>

    <h2>Industry</h2>
    <ul className="entries">
      {industry.map(e => <Entry key={e.title} {...e} />)}
    </ul>

    <h2>Teaching</h2>
    <p className="lead-in">I've enjoyed serving as a Teaching Assistant for the following courses.</p>
    <h3>At CMU</h3>
    <Courses courses={cmuTeaching} />

    <h3>At Cornell</h3>
    <Courses courses={cornellTeaching} />

    <h2>Service</h2>
    <ul>
      <li>Co-Reviewer, POPL 2026</li>
    </ul>
  </div>
);

export default App;
